"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import H1 from "@/components/typography/h1";
import { useBackgroundToggle } from "@/components/background-toggle";
import { cn } from "@/lib/utils";

const META = ["Niagara, ON", "Internal systems", "Automation"];

export default function ScrollHero() {
    const reduce = useReducedMotion();
    const { enabled, toggle } = useBackgroundToggle();

    const container = {
        hidden: {},
        show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
    };
    const item = {
        hidden: reduce ? { opacity: 0 } : { opacity: 0, y: 18 },
        show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
    };

    return (
        <section
            id="hero"
            className="relative flex min-h-[88svh] flex-col justify-center overflow-hidden px-4 py-20 sm:px-16"
        >
            <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="mx-auto w-full max-w-5xl"
            >
                <motion.div variants={item} className="mb-8 flex flex-wrap items-center gap-x-3 gap-y-2">
                    {META.map((m, i) => (
                        <span key={m} className="flex items-center gap-3">
                            {i > 0 && <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />}
                            <span className="font-mono text-[0.7rem] uppercase tracking-[0.25em] text-muted-foreground sm:text-xs">
                                {m}
                            </span>
                        </span>
                    ))}
                </motion.div>

                <motion.div variants={item}>
                    {/* The visible name is split around the hidden background
                        toggle (the "o"), which would otherwise make the heading
                        read as "J, button, sh Wood". aria-labelledby gives the
                        heading its clean name; the letters are hidden from
                        assistive tech while the toggle stays exposed and
                        focusable, announced as its own button. */}
                    <H1
                        aria-labelledby="hero-heading-name"
                        className="text-left text-[3.25rem] leading-[0.92] sm:text-[8.5rem]"
                    >
                        <span id="hero-heading-name" className="sr-only">
                            Josh Wood, Full-Stack Developer in Niagara, Ontario
                        </span>
                        <span className="block">
                            <span aria-hidden="true">J</span>
                            <button
                                type="button"
                                onClick={toggle}
                                aria-label="Animated background"
                                aria-pressed={enabled}
                                className="inline cursor-default rounded-lg bg-transparent p-0 font-inherit outline-none transition-[color,box-shadow] hover:text-primary focus-visible:text-primary focus-visible:ring-[3px] focus-visible:ring-ring/50"
                            >
                                <span aria-hidden="true">o</span>
                            </button>
                            <span aria-hidden="true">sh Wood</span>
                        </span>
                        <span
                            aria-hidden="true"
                            className="mt-4 block max-w-3xl font-sans text-xl font-normal leading-tight text-muted-foreground sm:text-4xl"
                        >
                            Full-Stack Developer in Niagara, Ontario
                        </span>
                    </H1>
                </motion.div>

                <motion.p
                    variants={item}
                    className={cn(
                        "mt-6 max-w-2xl text-xl leading-snug text-muted-foreground sm:text-3xl sm:leading-tight"
                    )}
                >
                    I build software that holds up in the real world —{" "}
                    <span className={cn("text-foreground", enabled && "sm:dark:text-primary")}>
                        front end to infrastructure.
                    </span>
                </motion.p>

                <motion.a
                    variants={item}
                    href="#story"
                    className="mt-12 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground transition-colors hover:text-foreground"
                >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-border">
                        <motion.span
                            animate={reduce ? {} : { y: [0, 4, 0] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <ArrowDown className="h-4 w-4" />
                        </motion.span>
                    </span>
                    Scroll to read
                </motion.a>
            </motion.div>
        </section>
    );
}
